import React, { useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';

import { css } from '@emotion/react';

import * as theme from '@gumtree/ui-library/src/base/theme';
import { trackV2 } from '@gumtree/shared/src/util/track-ga-event';
import { Toggle, Toggler, ToggleContent } from '@gumtree/ui-library';
import { VipState } from '../../reducers';
import { newLayout } from '../../app.style';

type ToggleProps = React.ComponentProps<typeof Toggle>;

const StandardisedFeatures: React.FC<Props> = ({
    isMobileCardsLayout = false,
    hideTitle = false,
    previewCount = 12,
}) => {
    const [showAll, setShowAll] = useState(false);
    const wasEverExpanded = useRef(false);

    const features = useSelector(
        ({ adDetails: { attributes } }: VipState) =>
            ('attributeGroups' in attributes &&
                attributes.attributeGroups.find(({ id }) => id === 'cars4')?.attributes) ||
            null
    );

    const expandFeatures = useCallback<ToggleProps['onToggle']>(({ isOpen }) => {
        setShowAll((x) => !x);
        if (isOpen && !wasEverExpanded.current) {
            wasEverExpanded.current = true;
            trackV2('expand-vehicle-features');
        }
    }, []);
    const useToggle = features && features.length > previewCount;
    return features ? (
        <Root isMobileCardsLayout={isMobileCardsLayout}>
            {!hideTitle && <h2>Features</h2>}

            <Features>
                {features
                    .slice(0, useToggle && !showAll ? previewCount : undefined)
                    .map((attrib) => {
                        const { key, name } = attrib;
                        return (
                            <div key={key} title={name}>
                                {name}
                            </div>
                        );
                    })}
            </Features>

            {useToggle && (
                <Toggle onToggle={expandFeatures}>
                    <StyledToggler>{showAll ? 'Show less' : 'Show more'}</StyledToggler>
                    <ToggleContent />
                </Toggle>
            )}
        </Root>
    ) : null;
};

interface Props {
    isMobileCardsLayout?: boolean;
    hideTitle?: boolean;
    previewCount?: number;
}

const Root = styled.section<{ isMobileCardsLayout: boolean }>`
    h2 {
        font-size: ${theme.fontSizes.large};

        ${theme.mediaQuery.from(theme.breakpoints.medium)} {
            font-size: ${theme.fontSizes.xlarge};
        }
        margin-bottom: ${theme.gutterSizes.medium};
    }

    ${({ isMobileCardsLayout }) =>
        isMobileCardsLayout &&
        css`
            ${newLayout}

            .toggle {
                display: flex;
                justify-content: flex-end;
            }

            padding: ${theme.gutterSizes.medium};
        `}
`;

const Features = styled.div`
    column-count: 2;
    column-width: 160px;

    > div {
        width: 160px;
    }

    ${theme.mediaQuery.from(theme.breakpoints.large)} {
        > div {
            width: 200px;
        }
        column-width: 200px;
        column-count: 3;
    }
`;

const StyledToggler = styled(Toggler)`
    color: ${theme.colors.blue};
    font-size: ${theme.fontSizes.medium};

    margin-top: ${theme.gutterSizes.medium};
    margin-bottom: ${theme.gutterSizes.medium};
    margin-right: ${theme.gutterSizes.base};
    user-select: none;

    > span {
        margin-left: ${theme.gutterSizes.base};
    }
`;

export default StandardisedFeatures;
